import { useEffect } from 'react';
import { useThemeStore } from '../stores/useThemeStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useAccessibilityStore } from '../stores/useAccessibilityStore';
import { themeApi } from '../services/themeApi';
import { seedDisplayCurrencyFromTenant } from '../utils/currency';
import { useQuery } from '@tanstack/react-query';

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
// Platform default dark palette when the tenant hasn't picked one.
// 'periwinkle-dark' is the house dark default; 'indigo-night' (the former
// default) remains selectable in the catalogue.
const DARK_FALLBACK_THEME_ID = 'periwinkle-dark';
// Default LIGHT palette when the tenant hasn't customised one.
// Without this, picking "Light" in the a11y panel did nothing visible:
// resolved stayed null, setCustomTheme was never called, and the dark
// CSS vars from the previous "Dark" pick stuck around. 'periwinkle' is the
// house light default; 'maei-brand' remains a named preset.
const LIGHT_FALLBACK_THEME_ID = 'periwinkle';

export const useTheme = () => {
  const {
    currentTheme,
    branding,
    setCustomTheme,
    setBranding,
    setTheme,
  } = useThemeStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Scope the theme fetch by active tenant so switching clinics in one
  // browser re-fetches that tenant's branding instead of reusing the prior
  // tenant's cached theme (prefix-matched by the invalidations elsewhere).
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  // Re-derive the effective scheme on every render of consumers (e.g.
  // SettingsPage) so a colour-scheme toggle in the a11y card flips the
  // tenant palette without a reload. The store-level applier handles
  // CSS vars; this hook handles the catalogue swap.
  const colorScheme = useAccessibilityStore((s) => s.prefs.colorScheme);

  // Hospital-config requires a resolved tenant context, so we only fetch
  // it after sign-in. Pre-auth branding lives on the login page, which
  // resolves its own tenant config via /public/tenant-config.
  //
  // Stale time is short so when the OWNER saves a new color/logo in
  // System Settings, every user's session picks it up on next focus
  // without waiting half an hour. The mutation itself invalidates this
  // query for the OWNER's own session (see useUpdateHospitalConfig).
  const { data: themeConfig } = useQuery({
    queryKey: ['theme-config', activeTenantId ?? ''],
    queryFn: themeApi.getThemeConfig,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  const availableThemes = useThemeStore((s) => s.availableThemes);

  useEffect(() => {
    if (!themeConfig) return;

    // Seed the per-browser currency cache from the tenant's saved
    // facility currency so billing amounts render in the right code on
    // every device — instead of reverting to the browser-locale guess
    // (en-GB → GBP) each fresh session.
    seedDisplayCurrencyFromTenant(themeConfig.billingCurrency);

    // Resolve which palette to apply, biased by the user's accessibility
    // colour-scheme pref:
    //   - dark (or system→dark): tenant.themeConfigDark > catalogue
    //     'indigo-night' theme > tenant.themeConfig fallback
    //   - light/system→light: tenant.themeConfig > themeId in catalogue
    //
    // The dark fallback to the catalogue 'indigo-night' theme means a
    // tenant that hasn't saved a custom dark palette still gets a
    // coherent dark UI when the user opts in.
    const wantsDark = isEffectiveDarkScheme(colorScheme);

    let resolved: typeof currentTheme | null = null;
    if (wantsDark) {
      // DARK resolution chain (most-specific first):
      //   1. themeConfigDark — reserved for a future custom-dark
      //      editor (no UI writes it today; column kept in schema).
      //   2. themeIdDark — catalogue id the OWNER picked from the
      //      "Dark theme" preset selector in System Settings → Branding.
      //   3. platform default 'indigo-night'
      resolved = (themeConfig.themeDark as typeof currentTheme | null) ?? null;
      if (!resolved && themeConfig.themeIdDark) {
        resolved =
          availableThemes.find((t) => t.id === themeConfig.themeIdDark) ?? null;
      }
      if (!resolved) {
        resolved =
          availableThemes.find((t) => t.id === DARK_FALLBACK_THEME_ID) ?? null;
      }
    } else {
      // LIGHT path. A saved NAMED preset resolves to the LIVE catalogue
      // palette FIRST — so platform palette updates (a brand tweak, etc.)
      // reach the tenant, exactly like the dark path is id-based. The saved
      // `theme` blob is a snapshot frozen at save time; it's authoritative
      // ONLY for a hand-tuned 'custom' theme. For a preset it's stale and
      // must not win (this is why light lagged behind dark after a palette
      // change — dark clears its blob on catalogue picks, light didn't).
      const lightId = themeConfig.themeId;
      if (
        lightId &&
        lightId !== 'custom' &&
        lightId !== DARK_FALLBACK_THEME_ID
      ) {
        resolved = availableThemes.find((t) => t.id === lightId) ?? null;
      }
      if (!resolved) {
        resolved = (themeConfig.theme as typeof currentTheme | null) ?? null;
      }
      if (!resolved) {
        resolved =
          availableThemes.find((t) => t.id === LIGHT_FALLBACK_THEME_ID) ??
          null;
      }
    }
    if (resolved) {
      setCustomTheme(resolved);
    }
    if (themeConfig.branding) {
      setBranding(themeConfig.branding);
      // When a full theme just landed via setCustomTheme, the palette
      // is already coherent. Only apply the standalone primary/accent
      // override when no full theme was resolved — otherwise the
      // tenant-branding colours would clobber two shades out of the
      // chosen palette and produce the incoherent mix the user saw.
      if (!resolved) {
        applyTenantColors(
          themeConfig.branding.primaryColor,
          themeConfig.branding.accentColor,
        );
      }
    }
  }, [themeConfig, setCustomTheme, setBranding, availableThemes, colorScheme]);

  // Apply theme CSS classes on mount and changes
  useEffect(() => {
    applyThemeClasses(currentTheme.id);
  }, [currentTheme]);

  return {
    theme: currentTheme,
    branding,
    setTheme,
    // Only "loading" when the query is enabled AND hasn't resolved yet.
    // Pre-auth there's nothing to wait for — defaults apply immediately.
    isLoading: isAuthenticated && !themeConfig,
  };
};

function applyTenantColors(
  primary: string | undefined,
  accent: string | undefined,
) {
  const root = document.documentElement;
  if (primary && HEX_COLOR_RE.test(primary)) {
    root.style.setProperty('--color-primary-600', primary);
    root.style.setProperty('--color-primary-700', primary);
  }
  if (accent && HEX_COLOR_RE.test(accent)) {
    root.style.setProperty('--color-accent', accent);
  }
}

function applyThemeClasses(themeId: string) {
  const body = document.body;
  body.classList.remove(
    'theme-modern-blue',
    'theme-medical-green',
    'theme-professional-purple',
    'theme-dark-mode',
  );
  body.classList.add(`theme-${themeId}`);
}

export function isEffectiveDarkScheme(
  scheme: 'system' | 'light' | 'dark',
): boolean {
  if (scheme === 'dark') return true;
  if (scheme === 'light') return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}
